import React from "react";
import { Helmet } from "react-helmet-async";

export default function LegersCoffee() {
	const titlePage =
		"Léger's Coffee | Site vitrine coffee shop Annecy | Weblax Studio";
	const description =
		"Weblax a conçu le site vitrine de Léger's Coffee, coffee shop à Annecy : identité digitale artisanale, carte des boissons, agenda des événements et SEO local. +95% de trafic organique et +60% de fréquentation nouvelle clientèle.";
	const url = "https://weblax.fr/realisations/legers-coffee";

	const features = [
		{
			icon: "☕",
			title: "Carte des boissons & menus",
			desc: "Présentation visuelle et appétissante de toute l'offre : cafés de spécialité, boissons signature, pâtisseries du jour. Mise à jour facilitée par l'équipe sans compétences techniques.",
		},
		{
			icon: "📅",
			title: "Agenda des événements",
			desc: "Module d'événements intégré pour les cupping sessions, ateliers barista, soirées thématiques et pop-ups — avec inscription directe et partage sur les réseaux sociaux.",
		},
		{
			icon: "📍",
			title: "SEO local Annecy",
			desc: "Stratégie de référencement ciblée : fiche Google Business optimisée, balisage schema.org CafeOrCoffeeShop, contenu optimisé pour 'coffee shop Annecy', 'café spécialité Haute-Savoie'.",
		},
		{
			icon: "📸",
			title: "Galerie immersive",
			desc: "Direction photographique et mise en scène des visuels : latte art, grains de café, intérieur chaleureux — pour donner envie aux visiteurs de pousser la porte avant même d'être sur place.",
		},
		{
			icon: "📱",
			title: "Mobile-first local",
			desc: "Site pensé pour les personnes qui cherchent un café sur leur téléphone en se promenant à Annecy. Chargement instantané, horaires et adresse accessibles en un coup d'œil.",
		},
		{
			icon: "🛍️",
			title: "Boutique en ligne (cafés)",
			desc: "Section e-commerce légère pour commander des grains de café de spécialité en ligne — un canal de revenus additionnel sans complexité logistique excessive.",
		},
	];

	const results = [
		{
			icon: "📈",
			metric: "+95%",
			label: "Trafic organique",
			color: "text-amber-700",
			bg: "from-amber-50 to-yellow-50",
			border: "border-amber-100",
		},
		{
			icon: "🆕",
			metric: "+60%",
			label: "Nouvelle clientèle",
			color: "text-emerald-600",
			bg: "from-emerald-50 to-green-50",
			border: "border-emerald-100",
		},
		{
			icon: "🔍",
			metric: "Top 5",
			label: "Coffee shop Annecy",
			color: "text-blue-600",
			bg: "from-blue-50 to-sky-50",
			border: "border-blue-100",
		},
		{
			icon: "📅",
			metric: "×4",
			label: "Inscriptions événements",
			color: "text-rose-600",
			bg: "from-rose-50 to-pink-50",
			border: "border-rose-100",
		},
	];

	return (
		<>
			<Helmet>
				<title>{titlePage}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={titlePage} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={url} />
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: "Léger's Coffee — Site vitrine coffee shop",
						description,
						url,
						dateCreated: "2024",
						provider: {
							"@type": "Organization",
							name: "Weblax",
							url: "https://weblax.fr",
						},
						about: {
							"@type": "CafeOrCoffeeShop",
							name: "Léger's Coffee",
							address: {
								"@type": "PostalAddress",
								addressLocality: "Annecy",
								addressCountry: "FR",
							},
							servesCuisine: "Café de spécialité",
						},
					})}
				</script>
			</Helmet>

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-white to-stone-50 border-b border-border">
				<div
					className="absolute inset-0 opacity-20"
					style={{
						backgroundImage:
							"radial-gradient(circle at 15% 55%, #fef3c7 0%, transparent 50%), radial-gradient(circle at 85% 30%, #e7e5e4 0%, transparent 50%)",
					}}
				/>
				<div className="weblax-container py-20 relative">
					<div className="flex flex-wrap items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
							☕ Coffee shop
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
							✅ Projet livré
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
							📍 Annecy, France
						</span>
					</div>

					<h1 className="weblax-heading-display mb-6 max-w-4xl">
						Léger's Coffee — Le coffee shop d'Annecy qui{" "}
						<span className="text-amber-700">
							attire de nouveaux clients chaque jour
						</span>
					</h1>

					<p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-10">
						Weblax a conçu le site vitrine de Léger's Coffee, un coffee shop
						artisanal spécialisé en cafés de spécialité à Annecy. Un projet
						pensé pour ancrer l'établissement dans le paysage digital local,
						attirer une clientèle curieuse et passionnée, et donner envie de
						pousser la porte avant même d'y être.
					</p>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						{results.map((r) => (
							<div
								key={r.label}
								className="bg-white/80 backdrop-blur border border-border rounded-xl p-5 text-center shadow-sm"
							>
								<div className="text-2xl mb-1">{r.icon}</div>
								<div className={`text-3xl font-bold mb-1 ${r.color}`}>
									{r.metric}
								</div>
								<div className="text-xs text-muted-foreground">{r.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<main className="weblax-container py-20 space-y-24">
				{/* ── Contexte ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Contexte
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Un coffee shop artisanal sans présence digitale
					</h2>
					<div className="grid md:grid-cols-2 gap-10">
						<div className="space-y-4 text-muted-foreground leading-relaxed">
							<p>
								Léger's Coffee est un coffee shop indépendant à Annecy, porté
								par une passion sincère pour les cafés de spécialité, le latte
								art et l'accueil chaleureux. Un lieu qui fidélise rapidement sa
								clientèle de quartier — mais qui peinait à attirer de nouveaux
								visiteurs faute de visibilité en ligne.
							</p>
							<p>
								Sans site web, les recherches Google renvoyaient vers des
								concurrents mieux référencés. Les événements organisés par le
								café (ateliers, cuppings, soirées) ne trouvaient pas leur public
								en dehors des habitués. Et la spécificité de l'offre — cafés de
								spécialité en provenance directe de torréfacteurs sélectionnés —
								n'était pas communiquée aux curieux de passage à Annecy.
							</p>
							<p>
								L'enjeu : créer une présence digitale qui reflète l'âme du lieu,
								attire une clientèle sensible à la qualité et booste la
								visibilité locale sur Google.
							</p>
						</div>
						<div className="space-y-3">
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
								Situation initiale
							</div>
							{[
								{
									label: "Site web",
									value: "Inexistant",
									color: "text-red-500",
								},
								{
									label: "Fiche Google Business",
									value: "Non optimisée",
									color: "text-orange-500",
								},
								{
									label: "Visibilité 'coffee shop Annecy'",
									value: "Absente",
									color: "text-red-500",
								},
								{
									label: "Communication événements",
									value: "Réseaux sociaux uniquement",
									color: "text-orange-500",
								},
								{
									label: "Vente de café en ligne",
									value: "Impossible",
									color: "text-red-500",
								},
							].map((item) => (
								<div
									key={item.label}
									className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30"
								>
									<span className="text-sm text-muted-foreground">
										{item.label}
									</span>
									<span className={`text-sm font-semibold ${item.color}`}>
										{item.value}
									</span>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Brief ── */}
				<section className="bg-gradient-to-r from-amber-50 to-stone-50 border border-amber-100 rounded-2xl p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Objectifs
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Ce que Léger's Coffee attendait de Weblax
					</h2>
					<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{[
							{
								icon: "🌐",
								text: "Un site vitrine qui reflète l'ambiance chaleureuse et artisanale du coffee shop",
							},
							{
								icon: "📍",
								text: "Apparaître sur Google quand quelqu'un cherche un café à Annecy",
							},
							{
								icon: "📅",
								text: "Un module événements pour promouvoir les ateliers et cuppings sessions",
							},
							{
								icon: "☕",
								text: "Une carte boissons attrayante et facile à mettre à jour",
							},
							{
								icon: "📱",
								text: "Une expérience mobile parfaite pour les touristes et locaux",
							},
							{
								icon: "🛍️",
								text: "La possibilité de vendre des cafés en ligne pour étendre l'activité",
							},
						].map((item) => (
							<div
								key={item.text}
								className="flex items-start gap-3 bg-white rounded-xl p-4 border border-amber-100 shadow-sm"
							>
								<span className="text-xl shrink-0">{item.icon}</span>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{item.text}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Fonctionnalités ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Solution
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Un site vitrine qui sent bon le café
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Pour un coffee shop artisanal, le site web doit transmettre une
						atmosphère avant de vendre. L'utilisateur doit ressentir la chaleur
						du lieu, la qualité de l'offre et l'authenticité de l'équipe — avant
						même d'avoir posé son téléphone.
					</p>
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{features.map((f) => (
							<div
								key={f.title}
								className="group p-6 rounded-2xl border border-border hover:border-amber-200 hover:shadow-md transition-all duration-200 bg-white"
							>
								<span className="text-3xl mb-4 block">{f.icon}</span>
								<h3 className="font-semibold text-foreground mb-2">
									{f.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Processus ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Déroulement
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-10">
						Notre méthode pour ce projet
					</h2>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								step: "01",
								title: "Immersion & identité",
								desc: "Visite du coffee shop, sessions photo, analyse de l'univers de marque et des valeurs portées par Léger's Coffee pour créer un site authentique.",
								tag: "Semaine 1",
							},
							{
								step: "02",
								title: "Design & direction artistique",
								desc: "Création de l'identité digitale, palette chaude et artisanale, typographie soignée et mise en page centrée sur les visuels et l'atmosphère.",
								tag: "Semaine 2",
							},
							{
								step: "03",
								title: "Développement",
								desc: "Intégration React, carte des boissons dynamique, module événements, boutique café en ligne et optimisation des performances mobiles.",
								tag: "Semaines 3-4",
							},
							{
								step: "04",
								title: "SEO local & lancement",
								desc: "Optimisation Google Business Profile, balisage schema.org, création de contenu ciblé Annecy et déploiement avec suivi des positions.",
								tag: "Semaine 5",
							},
						].map((item) => (
							<div
								key={item.step}
								className="p-6 rounded-2xl border border-border bg-white"
							>
								<div className="flex items-center justify-between mb-3">
									<span className="text-2xl font-bold text-amber-200">
										{item.step}
									</span>
									<span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
										{item.tag}
									</span>
								</div>
								<h3 className="font-semibold text-foreground mb-2">
									{item.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{item.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Stack ── */}
				<section className="bg-muted/30 rounded-2xl border border-border p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Stack technique
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">Technologies utilisées</h2>
					<div className="flex flex-wrap gap-3">
						{[
							{ name: "React", desc: "Interface dynamique" },
							{ name: "Next.js", desc: "SSR & SEO" },
							{ name: "Tailwind CSS", desc: "Design system" },
							{ name: "Schema.org", desc: "CafeOrCoffeeShop" },
							{ name: "Stripe", desc: "Vente café en ligne" },
							{ name: "Sanity CMS", desc: "Gestion contenu" },
							{ name: "Vercel", desc: "Déploiement" },
						].map((tech) => (
							<div
								key={tech.name}
								className="flex items-center gap-2 bg-white border border-border rounded-lg px-4 py-2.5 shadow-sm"
							>
								<span className="font-semibold text-sm text-foreground">
									{tech.name}
								</span>
								<span className="text-xs text-muted-foreground">
									— {tech.desc}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Résultats ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Impact
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						De nouveaux visages chaque semaine
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Deux mois après le lancement, Léger's Coffee attire régulièrement de
						nouveaux clients qui ont découvert le café via Google — des
						touristes en visite à Annecy, des habitants du quartier qui ne le
						connaissaient pas encore, et des passionnés de café de spécialité
						qui cherchaient une adresse de confiance.
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								metric: "+95%",
								detail: "de trafic organique",
								desc: "Le site capte désormais des visiteurs qualifiés via Google sur des requêtes comme 'coffee shop Annecy', 'café spécialité Annecy' et 'café latte art Haute-Savoie'.",
								color: "text-amber-700",
								bg: "from-amber-50 to-yellow-50",
								border: "border-amber-100",
							},
							{
								metric: "+60%",
								detail: "de nouvelle clientèle",
								desc: "6 clients sur 10 mentionnent avoir découvert Léger's Coffee via le site ou Google — une transformation radicale de l'acquisition client.",
								color: "text-emerald-600",
								bg: "from-emerald-50 to-green-50",
								border: "border-emerald-100",
							},
							{
								metric: "Top 5",
								detail: "sur 'coffee shop Annecy'",
								desc: "Positionnement SEO local atteint sur les principales requêtes café à Annecy, devant de nombreux concurrents plus anciens.",
								color: "text-blue-600",
								bg: "from-blue-50 to-sky-50",
								border: "border-blue-100",
							},
							{
								metric: "×4",
								detail: "d'inscriptions aux événements",
								desc: "Le module événements a transformé la communication des ateliers barista et cuppings — avec 4 fois plus de participants qu'avant le site.",
								color: "text-rose-600",
								bg: "from-rose-50 to-pink-50",
								border: "border-rose-100",
							},
						].map((r) => (
							<div
								key={r.metric}
								className={`bg-gradient-to-br ${r.bg} border ${r.border} rounded-2xl p-8`}
							>
								<div className={`text-5xl font-bold mb-2 ${r.color}`}>
									{r.metric}
								</div>
								<div className="font-semibold text-foreground mb-3">
									{r.detail}
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{r.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Citation ── */}
				<section className="border border-amber-100 bg-gradient-to-br from-amber-50 to-stone-50 rounded-2xl p-10 text-center">
					<span className="text-4xl mb-6 block">☕</span>
					<blockquote className="text-xl font-medium text-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
						"Des gens entrent maintenant en disant 'j'ai vu votre site, j'avais
						envie de venir'. C'est exactement ce qu'on voulait — que le site
						donne envie avant même d'arriver."
					</blockquote>
					<cite className="text-sm text-muted-foreground not-italic">
						Fondateur Léger's Coffee — Annecy
					</cite>
				</section>

				{/* ── Conclusion + CTA ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Conclusion
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Pour un commerce local, la visibilité digitale est une nécessité
					</h2>
					<div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed mb-12">
						<p>
							Léger's Coffee prouve qu'un site vitrine bien conçu peut
							transformer radicalement la dynamique d'acquisition client d'un
							commerce local. Avant le site, le café dépendait du
							bouche-à-oreille et du passage. Après : Google travaille en
							continu pour amener de nouveaux clients.
						</p>
						<p>
							Pour les commerces de proximité, artisans et indépendants,
							l'investissement dans un site web optimisé pour le SEO local est
							souvent le plus rentable qu'ils puissent faire — avec un retour
							mesurable dès les premières semaines.
						</p>
						<p>
							C'est cette transformation que Weblax a réalisée pour Léger's
							Coffee — et que nous pouvons réaliser pour votre commerce.
						</p>
					</div>

					<div className="bg-foreground text-background rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
						<div>
							<h3 className="text-xl font-bold mb-2">
								Votre commerce mérite d'être trouvé sur Google
							</h3>
							<p className="text-sm opacity-70">
								Site vitrine, SEO local, identité digitale — parlons de votre
								projet.
							</p>
						</div>
						<a
							href="/contact"
							className="shrink-0 bg-white text-foreground font-semibold px-8 py-3 rounded-xl hover:bg-amber-50 transition-colors text-sm whitespace-nowrap"
						>
							Démarrer un projet →
						</a>
					</div>
				</section>
			</main>
		</>
	);
}
